// Maps each resource's status enum to the EXACT labels/variants used in the
// kanonical Status component at src/components/special/Status.jsx.
// Variants resolve to color + icon via getStatusStyle() below.
// Keep this file in sync with Status.jsx — both must show the same label for
// the same status value, otherwise the overview card and the table disagree.

import HourglassFullIcon from '@mui/icons-material/HourglassFull';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder';
import FlagCircleIcon from '@mui/icons-material/FlagCircle';
import ModeEditIcon from '@mui/icons-material/ModeEdit';

export const STATUS_DEFINITIONS = {
  // OrderStatusEnum: OPEN=0, FINISHED=1, CANCELLED=2, MAIL_SENT=3
  // (matches Status.jsx OrderStatus exactly)
  orders: {
    0: { key: 'OPEN', labelKey: 'Created', variant: 'open' },
    3: { key: 'MAIL_SENT', labelKey: 'Sent', variant: 'sent' },
    1: { key: 'FINISHED', labelKey: 'Finished', variant: 'finished' },
    2: { key: 'CANCELLED', labelKey: 'Canceled', variant: 'rejected' },
  },
  // InvoiceStatusEnum: CREATED=0, PAYED=1, MAIL_SENT=2, IN_ARREARS=3,
  // REFUSAL_TO_PAY=4, CANCELLED=5 (set when a cancellation invoice is created).
  invoices: {
    0: { key: 'CREATED', labelKey: 'Created', variant: 'open' },
    2: { key: 'MAIL_SENT', labelKey: 'Sent', variant: 'sent' },
    1: { key: 'PAYED', labelKey: 'Payed', variant: 'accepted' },
    3: { key: 'IN_ARREARS', labelKey: 'In Arrears', variant: 'expired' },
    4: { key: 'REFUSAL_TO_PAY', labelKey: 'Refusal to Pay', variant: 'rejected' },
    5: { key: 'CANCELLED', labelKey: 'Invoice cancelled', variant: 'rejected' },
  },
  // OfferStatusEnum: MAIL_SENT=0, ACCEPTED=1, FINISHED=2, REJECTED=3, CREATED=4
  // FINISHED(2) is intentionally omitted: it's not a meaningful filter/settable
  // status for offers. Offers that ARE finished still render correctly in the
  // table via Status.jsx; they just can't be filtered/set from the card.
  offers: {
    4: { key: 'CREATED', labelKey: 'Created', variant: 'open' },
    0: { key: 'MAIL_SENT', labelKey: 'Sent', variant: 'sent' },
    1: { key: 'ACCEPTED', labelKey: 'Accepted', variant: 'accepted' },
    3: { key: 'REJECTED', labelKey: 'Rejected', variant: 'rejected' },
  },
  // ContractStatusEnum (backend names): SENT=0, ACCEPTED=1, REJECTED=2, SETTLED=3, CREATED=4
  // Status.jsx ContractStatus uses different labels — we mirror Status.jsx for UI consistency.
  contracts: {
    0: { key: 'SENT', labelKey: 'Open', variant: 'open' },
    1: { key: 'ACCEPTED', labelKey: 'Accepted', variant: 'accepted' },
    2: { key: 'REJECTED', labelKey: 'Rejected', variant: 'rejected' },
    3: { key: 'SETTLED', labelKey: 'Created', variant: 'draft' },
    4: { key: 'CREATED', labelKey: 'Finished', variant: 'finished' },
  },
};

// Canonical variant → color + icon mapping. Mirrors Status.jsx's switch statement.
// Color shades are theme-derived (primary/blueAccent/...) and must be looked up
// via tokens(theme.palette.mode, theme.palette.colorTheme).
export const getStatusStyle = (colors, variant) => {
  switch (variant) {
    case 'draft':
      return { color: colors.blueAccent?.[600], Icon: ModeEditIcon };
    case 'open':
      return { color: colors.orangeAccent?.[600], Icon: HourglassFullIcon };
    case 'sent':
      return { color: colors.blueAccent?.[600], Icon: EmailIcon };
    case 'accepted':
      return { color: colors.greenAccent?.[600], Icon: CheckCircleIcon };
    case 'rejected':
      return { color: colors.redAccent?.[600], Icon: CancelIcon };
    case 'expired':
      return { color: colors.redAccent?.[600], Icon: QueryBuilderIcon };
    case 'finished':
      return { color: colors.grey?.[600], Icon: FlagCircleIcon };
    default:
      return { color: colors.grey?.[600], Icon: HourglassFullIcon };
  }
};

export const getStatusColor = (colors, resource, status) => {
  const def = STATUS_DEFINITIONS[resource]?.[status];
  if (!def) return colors.grey?.[600] ?? '#8a8a8a';
  return getStatusStyle(colors, def.variant).color;
};

// Explicit display order per resource. JS iterates integer object keys in
// ascending numeric order, which is NOT the desired chip/donut order — so the
// order is controlled here. "Created" leads each pipeline.
export const STATUS_ORDER = {
  orders: [0, 3, 1, 2], // Created, Sent, Finished, Cancelled
  offers: [4, 0, 1, 3], // Created, Sent, Accepted, Rejected
  invoices: [0, 2, 1, 3, 4, 5], // Created, Sent, Paid, In Arrears, Refusal, Cancelled
  contracts: [0, 1, 2, 3, 4],
};

export const getStatusOrder = (resource) =>
  STATUS_ORDER[resource] ??
  Object.keys(STATUS_DEFINITIONS[resource] || {}).map(Number);

export const getStatusOptions = (resource) => {
  const defs = STATUS_DEFINITIONS[resource] || {};
  return getStatusOrder(resource)
    .filter((status) => defs[status])
    .map((status) => ({
      status,
      key: defs[status].key,
      labelKey: defs[status].labelKey,
      variant: defs[status].variant,
    }));
};
